import { Vertical } from '@gravitate-js/excalibrr'
import { useRef, useState, useMemo } from 'react'
import type { GridApi } from 'ag-grid-community'
import type { Match } from '../../../api/useSupplierReconciliation/types/match'
import type { SupplierReconciliationMetadataResponse } from '../../../api/useMetadata/types'
import { useSecurityContext } from '../../Navigation/SecurityContext'
import { useMasterMetadata } from '../../../api/useMetadata/useMasterMetadata'
import { useMatchView, useMatchActions } from './hooks'
import { MatchViewHeader } from './components/MatchViewHeader'
import { MatchViewGrid } from './components/MatchViewGrid'

export function MatchView() {
  const { scopes } = useSecurityContext()

  // Modal state
  const [selectedMatchForBol, setSelectedMatchForBol] = useState<Match | null>(null)
  const [bolModalOpen, setBolModalOpen] = useState(false)

  // Centralized metadata call for all MatchView components
  const metadataQuery = useMasterMetadata<SupplierReconciliationMetadataResponse>('supplier_reconciliation')

  // Minimal timezone resolver for backward compatibility - now the API provides timezone_data directly
  const getTimezoneForMatch = useMemo(() => {
    // Return a function that indicates timezone should come from API data
    return (): undefined => {
      // This function is kept for compatibility but timezone resolution now happens
      // in the column renderers using API data (timezone_data.terminal, bol_date_local)
      return undefined
    }
  }, [])

  // Use the main view hook for business logic
  const {
    dateRange,
    setDateRange,
    activeStatus,
    setActiveStatus,
    serverFilters,
    setServerFilters,
    assignedToMe,
    setAssignedToMe,
    selectedRows,
    setSelectedRows,
    isVoidedViewActive,
    setIsVoidedViewActive,
    // DateRangeFilter props
    dateFilters,
    setDateFilters,
    dayRangeStorage,
    setDayRangeStorage,
    // Default helpers for reset functionality
    getDefaultApprovedDayRangeStorage,
    getDefaultVoidedDayRangeStorage,
    getDefaultOtherTabsDayRangeStorage,
    filteredData,
    statusCounts,
    isLoading,
    error,
    refetch,
    invoiceDueFilter,
  } = useMatchView()

  // Grid reference
  const gridAPIRef = useRef<GridApi<Match> | null>(null)

  // Use the actions hook for mutations and handlers
  const {
    handleMerge,
    handleBulkApprove,
    handleBulkHold,
    handleIndividualApprove,
    handleIndividualHold,
    handleIndividualUnhold,
    handleVoidInvoice,
    handleUnvoidInvoice,
    handleCreateInvoice,
  } = useMatchActions({ onRefetch: refetch, gridAPIRef })

  // Single row action handlers for grid actions
  const handleApproveMatch = async (match: Match, reason: string) => {
    await handleIndividualApprove(match.id, reason)
  }

  const handleHoldMatch = async (match: Match, reason?: string) => {
    await handleIndividualHold(match.id, reason)
  }

  const handleUnholdMatch = async (match: Match) => {
    await handleIndividualUnhold(match.id)
  }

  const handleSearchMatch = async (match: Match) => {
    setSelectedMatchForBol(match)
    setBolModalOpen(true)
  }

  const handleVoidInvoiceAction = async (invoiceId: string) => {
    await handleVoidInvoice(invoiceId)
  }

  const handleUnvoidInvoiceAction = async (invoiceId: string) => {
    await handleUnvoidInvoice(invoiceId)
  }

  const handleBulkCancel = () => {
    setSelectedRows([])
    if (gridAPIRef.current) {
      gridAPIRef.current.deselectAll()
    }
  }

  return (
    <Vertical style={{ height: '100%', overflow: 'hidden' }}>
      {/* Status Tabs with Header */}
      <MatchViewHeader
        activeStatus={activeStatus}
        setActiveStatus={setActiveStatus}
        statusCounts={statusCounts}
        isLoading={isLoading}
        isVoidedViewActive={isVoidedViewActive}
        setIsVoidedViewActive={setIsVoidedViewActive}
        filteredData={filteredData}
      />

      {/* Main Grid with Toolbar and Bulk Actions */}
      <MatchViewGrid
        filteredData={filteredData}
        isLoading={isLoading}
        gridAPIRef={gridAPIRef}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        serverFilters={serverFilters}
        setServerFilters={setServerFilters}
        dateRange={dateRange}
        setDateRange={setDateRange}
        // DateRangeFilter props
        dateFilters={dateFilters}
        setDateFilters={setDateFilters}
        dayRangeStorage={dayRangeStorage}
        setDayRangeStorage={setDayRangeStorage}
        assignedToMe={assignedToMe ?? false}
        setAssignedToMe={setAssignedToMe}
        // Tab state for dynamic slider range
        isVoidedViewActive={isVoidedViewActive}
        activeStatus={activeStatus}
        // Default helpers for reset functionality
        getDefaultApprovedDayRangeStorage={getDefaultApprovedDayRangeStorage}
        getDefaultVoidedDayRangeStorage={getDefaultVoidedDayRangeStorage}
        getDefaultOtherTabsDayRangeStorage={getDefaultOtherTabsDayRangeStorage}
        handleApproveMatch={handleApproveMatch}
        handleHoldMatch={handleHoldMatch}
        handleUnholdMatch={handleUnholdMatch}
        handleSearchMatch={handleSearchMatch}
        handleVoidInvoiceAction={handleVoidInvoiceAction}
        handleUnvoidInvoiceAction={handleUnvoidInvoiceAction}
        handleBulkCancel={handleBulkCancel}
        handleBulkApprove={handleBulkApprove}
        handleBulkHold={handleBulkHold}
        handleMerge={handleMerge}
        handleCreateInvoice={handleCreateInvoice}
        metadata={metadataQuery.data}
        metadataLoading={metadataQuery.isLoading}
        scopes={scopes}
        getTimezoneForMatch={getTimezoneForMatch}
        invoiceDueFilter={invoiceDueFilter}
      />
    </Vertical>
  )
}
